// Ch83 · options to send texts, especially the opening text.
//
// Grant's ruling on #540: yes to the design, and "the best thing here is to
// enhance our templates etc, so we have options to send texts (especially the
// opening text)". #540 shipped ONE generated opener. A locator texting twenty
// people a day does not want one sentence; they want a shelf to pick from.
//
// STORE DECISION — reuse, do not invent. LN Pro already has all of it:
//   · `email_templates` with a `channel` column holding email AND sms
//     (migration 007), a `template_type` purpose, per-user rows, and a
//     one-default-per-partition index enforced in the database
//   · `templateService` to read and write them
//   · `resolveTemplate` / `templateInterpolation` for {{placeholders}}
//   · Settings → Templates, an operator-maintainable UI that already exists
//
// A new `leads_sms_templates` table would have duplicated every one of those
// and needed a migration, a backend deploy, and a second editing surface for
// operators to learn. The one thing genuinely missing is CONTENT: migration 010
// seeds an sms/follow_up template, and nothing seeds an sms OPENER — precisely
// the case Grant named.
//
// So openers ship as BUILT-INS: real copy, in code, reviewable in a diff,
// available the moment this deploys and with no empty state on day one. A saved
// template of the same purpose outranks the built-in, because an operator's own
// words beat ours. Editing one is Settings → Templates, already built.
import type { WorkRow } from "./leadsWork";
import { draftOpener } from "./leadsSend";
import { templateService } from "./templateService";
import { resolveTemplateForSms } from "./resolveTemplate";
import { extractTemplateVariables } from "./templateInterpolation";
import type { EmailTemplate } from "../types";

/** Which moment in the conversation a template is written for. */
export type TemplateMoment = "opening" | "follow_up";

export interface SendTemplate {
  id: string;
  name: string;
  /** Raw body, still carrying {{placeholders}}. */
  body: string;
  moment: TemplateMoment;
  /** "saved" outranks "builtin" — the operator's own words win. */
  source: "saved" | "builtin";
}

/**
 * Starter copy. Deliberately plain: a locator writing to a stranger about an
 * apartment does not sound like marketing, and a template that sounds like
 * marketing gets rewritten every time, which defeats the point.
 *
 * Fair-housing note: none of these reference or invite any protected
 * characteristic, and none describe who lives anywhere. They ask about
 * timing, budget, and area — the three things the locator actually needs.
 */
export const BUILT_IN_TEMPLATES: readonly SendTemplate[] = [
  {
    id: "builtin:opener-ask-both",
    name: "Opener · ask move-in + budget",
    body: "Hi {{client_first_name}}, thanks for reaching out about apartments! Do you still need a place, and what's your move-in date and budget? — {{agent_first_name}} at LocateNow",
    moment: "opening",
    source: "builtin",
  },
  {
    id: "builtin:opener-short",
    name: "Opener · short",
    body: "Hi {{client_first_name}}, this is {{agent_first_name}} with LocateNow — are you still looking for an apartment?",
    moment: "opening",
    source: "builtin",
  },
  {
    id: "builtin:opener-area",
    name: "Opener · confirm the area",
    body: "Hi {{client_first_name}}, {{agent_first_name}} with LocateNow. I can send you places that fit — which areas are you looking at, and when do you need to move?",
    moment: "opening",
    source: "builtin",
  },
  {
    id: "builtin:followup-no-reply",
    name: "Follow-up · no reply yet",
    body: "Hi {{client_first_name}}, just checking in on your apartment search — still looking? — {{agent_first_name}} at LocateNow",
    moment: "follow_up",
    source: "builtin",
  },
  {
    id: "builtin:followup-send-places",
    name: "Follow-up · offer to send places",
    body: "Hi {{client_first_name}}, I have a few places that fit what you described. Want me to send them over? — {{agent_first_name}} at LocateNow",
    moment: "follow_up",
    source: "builtin",
  },
];

/**
 * `template_type` is the store's vocabulary for purpose; this maps it onto the
 * only distinction the composer cares about. Anything not obviously a
 * follow-up is offered as an opener, because being offered a template at the
 * wrong moment is a smaller cost than a template you cannot find.
 */
function momentOf(templateType: string | null | undefined): TemplateMoment {
  return templateType === "follow_up" || templateType === "thank_you"
    ? "follow_up"
    : "opening";
}

/** The moment this row is at, from the stage the work list already computed. */
export function momentForRow(row: WorkRow): TemplateMoment {
  return row.stage === "unclaimed" ||
    row.stage === "claimed_uncontacted" ||
    row.stage === "converted_uncontacted"
    ? "opening"
    : "follow_up";
}

/**
 * Built-ins plus whatever this user has saved, ordered so the first entry is
 * the one to preselect: right moment before wrong moment, saved before
 * built-in.
 *
 * A failed read is NOT an error here. The composer must still open with
 * something to send — losing the ability to text because a template query
 * failed would be the tail wagging the dog — so built-ins stand alone and the
 * caller is told the read failed so it can say so.
 */
export async function loadSendTemplates(
  row: WorkRow,
): Promise<{ templates: SendTemplate[]; storeFailed: boolean }> {
  let saved: SendTemplate[] = [];
  let storeFailed = false;
  try {
    const rows: EmailTemplate[] = await templateService.listTemplates("sms");
    saved = rows
      .filter((r) => (r.body || "").trim())
      .map((r) => ({
        id: r.id,
        name: r.name || "Saved template",
        body: r.body,
        moment: momentOf(r.template_type),
        source: "saved" as const,
      }));
  } catch {
    storeFailed = true;
  }

  const want = momentForRow(row);
  const rank = (t: SendTemplate) =>
    (t.moment === want ? 0 : 2) + (t.source === "saved" ? 0 : 1);
  const all = [...saved, ...BUILT_IN_TEMPLATES].sort((a, b) => rank(a) - rank(b));
  return { templates: all, storeFailed };
}

export interface ResolvedSend {
  body: string;
  /**
   * Placeholders the row had no value for. They interpolate to "" — so
   * "Hi {{client_first_name}}," becomes "Hi ," and goes to a real person
   * unless somebody notices. The composer shows these.
   */
  unresolved: string[];
}

/**
 * Fill a template for this person.
 *
 * The context adapter is the reason this lives here rather than being a call
 * to resolveTemplateForSms directly: that resolver takes a CLIENT, and half
 * this work list is leads. A lead has one `name` string and a single budget
 * number, so it is adapted rather than passed through.
 */
export function resolveForRow(
  template: SendTemplate,
  row: WorkRow,
  senderName?: string | null,
): ResolvedSend {
  const parts = (row.person.name || "").trim().split(/\s+/).filter(Boolean);
  const client = {
    first_name: parts[0] ?? "",
    last_name: parts.slice(1).join(" "),
    min_budget: null,
    max_budget: row.person.budget,
  };
  const agent = { full_name: (senderName || "").trim() || null };

  const resolved = resolveTemplateForSms({
    template: { subject: "", body: template.body },
    client,
    agent,
  });

  const context: Record<string, unknown> = {
    client_first_name: client.first_name,
    client_last_name: client.last_name,
    client_full_name: row.person.name,
    budget_range: row.person.budget,
    agent_full_name: agent.full_name,
    agent_first_name: (agent.full_name || "").split(/\s+/)[0] || "",
  };
  const unresolved = extractTemplateVariables(template.body).filter((key: string) => {
    const v = context[key];
    return v === null || v === undefined || String(v).trim() === "";
  });

  return { body: resolved.body.trim(), unresolved };
}

/**
 * What the composer opens with: the top-ranked template, or #540's generated
 * opener when there is genuinely nothing. The fallback is kept because a
 * composer that opens empty is a composer nobody uses.
 */
export function openingDraft(
  templates: SendTemplate[],
  row: WorkRow,
  senderName?: string | null,
): { body: string; templateId: string | null; unresolved: string[] } {
  const first = templates[0];
  if (!first) {
    return { body: draftOpener(row, senderName), templateId: null, unresolved: [] };
  }
  const { body, unresolved } = resolveForRow(first, row, senderName);
  return { body, templateId: first.id, unresolved };
}
